import { Effect } from 'effect';
import type { CatalogPluginDescriptor } from './descriptors.ts';
import { pluginDescriptors } from './plugin-descriptors.generated.ts';
import { legacyDescriptorSurfaces, type LegacyDescriptorSurface } from './legacy-surfaces.ts';
import type {
  CatalogItem,
  CatalogPort,
  CatalogRole,
  CatalogSection,
  CatalogStatus,
} from './index.ts';

type DescriptorCatalogShape = Pick<CatalogItem, 'section' | 'role' | 'port'>;
export type CatalogPluginRowSourceKind = 'descriptor' | 'legacy_surface';
export type CatalogPluginSourceFilter = 'all' | CatalogPluginRowSourceKind;
export type CatalogPluginRoleFilter = 'all' | 'parser' | 'source' | 'integration';

export interface CatalogPluginSourceTab {
  id: CatalogPluginSourceFilter;
  label: string;
}

export interface CatalogPluginRoleTab {
  id: CatalogPluginRoleFilter;
  label: string;
}

export type CatalogPluginTrustBadgeVariant = 'success' | 'warning';

export interface CatalogPluginTrustBadgeMeta {
  label: string;
  badgeVariant: CatalogPluginTrustBadgeVariant;
}

export interface FilterPluginCatalogRowsOptions {
  role?: string;
  status?: string;
  source?: string;
}

export const catalogPluginSourceTabs = [
  { id: 'all', label: 'All' },
  { id: 'descriptor', label: 'Descriptors' },
  { id: 'legacy_surface', label: 'Legacy' },
] as const satisfies readonly CatalogPluginSourceTab[];

export const catalogPluginRoleTabs = [
  { id: 'all', label: 'All' },
  { id: 'parser', label: 'Parsers' },
  { id: 'source', label: 'Sources' },
  { id: 'integration', label: 'Integrations' },
] as const satisfies readonly CatalogPluginRoleTab[];

export interface CatalogPluginRow {
  id: string;
  name: string;
  role: CatalogRole;
  status: CatalogStatus;
  description: string;
  href: string;
  port: CatalogPort | null;
  source_kind: CatalogPluginRowSourceKind;
  trust: CatalogPluginDescriptor['trust'] | 'unknown';
  runtime: CatalogPluginDescriptor['runtime'] | null;
  format: CatalogPluginDescriptor['format'] | null;
  allowed_hosts: readonly string[];
  npm_package: string | null;
  package_path: string | null;
  descriptor_id: string | null;
  legacy_ids: readonly string[];
  legacy_surface_ids: readonly string[];
  legacy_surface_paths: readonly string[];
  tags: readonly string[];
}

export interface LoadedEcosystemCatalog {
  catalogBySection: Record<CatalogSection, readonly CatalogItem[]>;
  allCatalogItems: readonly CatalogItem[];
  catalogById: Record<string, CatalogItem>;
  descriptorCatalogItems: readonly CatalogItem[];
  pluginCatalogRows: readonly CatalogPluginRow[];
  legacyDescriptorSurfaces: readonly LegacyDescriptorSurface[];
}

export interface LoadEcosystemCatalogOptions {
  sectionOrder: readonly CatalogSection[];
  catalogBySection: Record<CatalogSection, readonly CatalogItem[]>;
  descriptors?: readonly CatalogPluginDescriptor[];
  legacySurfaces?: readonly LegacyDescriptorSurface[];
}

function descriptorCatalogShape(descriptor: CatalogPluginDescriptor): DescriptorCatalogShape {
  if (descriptor.kind === 'parser') {
    return { section: 'parsers', role: 'parser', port: 'ParseDriver' };
  }
  if (descriptor.kind === 'source') {
    return { section: 'integrations', role: 'source', port: 'SourceConnector' };
  }
  return { section: 'integrations', role: 'integration', port: 'AutomationIntegration' };
}

function descriptorToCatalogItemUnsafe(descriptor: CatalogPluginDescriptor): CatalogItem {
  const catalog = descriptor.catalog ?? {};
  return {
    id: descriptor.id,
    name: catalog.name ?? descriptor.name ?? descriptor.id,
    status: 'supported',
    oneLiner: catalog.one_liner ?? descriptor.description ?? descriptor.id,
    href: `/plugins/${descriptor.id}`,
    source: { kind: 'descriptor', descriptorId: descriptor.id, derivation: 'derived' },
    allowedHosts: descriptor.allowed_hosts ?? [],
    tags: descriptor.tags,
    ...descriptorCatalogShape(descriptor),
    npmPackage: catalog.npm_package as CatalogItem['npmPackage'],
    packagePath: catalog.package_path as CatalogItem['packagePath'],
  };
}

export function descriptorToCatalogItemEffect(
  descriptor: CatalogPluginDescriptor,
): Effect.Effect<CatalogItem> {
  return Effect.sync(() => descriptorToCatalogItemUnsafe(descriptor));
}

export function descriptorToCatalogItem(descriptor: CatalogPluginDescriptor): CatalogItem {
  return Effect.runSync(descriptorToCatalogItemEffect(descriptor));
}

export function loadDescriptorCatalogItems(
  descriptors: readonly CatalogPluginDescriptor[] = pluginDescriptors,
): readonly CatalogItem[] {
  return descriptors.map(descriptorToCatalogItem);
}

function descriptorForItem(
  item: CatalogItem,
  descriptors: readonly CatalogPluginDescriptor[],
): CatalogPluginDescriptor | undefined {
  if (item.source.kind !== 'descriptor') return undefined;
  const { descriptorId } = item.source;
  return descriptors.find((descriptor) => descriptor.id === descriptorId);
}

interface LegacyPluginReference {
  legacyId: string;
  surface: LegacyDescriptorSurface;
}

function uniqueStrings(values: readonly string[]): readonly string[] {
  return [...new Set(values)];
}

function toPluginCatalogRow(
  item: CatalogItem,
  descriptor: CatalogPluginDescriptor,
  legacyReferences: readonly LegacyPluginReference[] = [],
): CatalogPluginRow {
  return {
    id: item.id,
    name: item.name,
    role: item.role,
    status: item.status,
    description: item.oneLiner,
    href: item.href,
    port: item.port ?? null,
    source_kind: 'descriptor',
    trust: descriptor.trust ?? 'unknown',
    runtime: descriptor.runtime ?? null,
    format: descriptor.format ?? null,
    allowed_hosts: item.allowedHosts ?? descriptor.allowed_hosts ?? [],
    npm_package: item.npmPackage ?? null,
    package_path: item.packagePath ?? null,
    descriptor_id: descriptor.id,
    legacy_ids: uniqueStrings(legacyReferences.map((reference) => reference.legacyId)),
    legacy_surface_ids: uniqueStrings(legacyReferences.map((reference) => reference.surface.id)),
    legacy_surface_paths: uniqueStrings(
      legacyReferences.map((reference) => reference.surface.path),
    ),
    tags: item.tags ?? descriptor.tags ?? [],
  };
}

export function loadPluginCatalogRows(
  items: readonly CatalogItem[],
  descriptors: readonly CatalogPluginDescriptor[] = pluginDescriptors,
  legacySurfaces: readonly LegacyDescriptorSurface[] = legacyDescriptorSurfaces,
): readonly CatalogPluginRow[] {
  const legacyReferencesByCatalogId = legacyPluginReferencesByCatalogId(legacySurfaces);
  const rows: CatalogPluginRow[] = [];
  for (const item of items) {
    const descriptor = descriptorForItem(item, descriptors);
    if (!descriptor) continue;
    rows.push(toPluginCatalogRow(item, descriptor, legacyReferencesByCatalogId.get(item.id) ?? []));
  }
  return rows;
}

const legacySurfaceShape: Record<
  LegacyDescriptorSurface['kind'],
  { role: CatalogRole; port: CatalogPort | null }
> = {
  'facet-registry': { role: 'parser', port: 'ParseDriver' },
  'openclaw-manifest': { role: 'integration', port: 'AutomationIntegration' },
  'runtime-manifest': { role: 'deploy', port: 'DeployTarget' },
};

const legacyPluginNameOverrides: Record<string, string> = {
  textlayer: 'Text layer',
  'llamaparse-fast': 'LlamaParse fast',
  'llamaparse-premium': 'LlamaParse premium',
  'llamaparse-agentic': 'LlamaParse agentic',
  'google-document-ai-ocr': 'Google Document AI OCR',
  'mistral-ocr-annotated': 'Mistral OCR annotated',
  'reducto-parse': 'Reducto parse',
  'reducto-agentic': 'Reducto agentic',
  mineru: 'MinerU',
  'gemini-3-flash-minimal': 'Gemini 3 Flash minimal',
  'vlm-parsebench': 'VLM ParseBench',
  'azure-di-layout': 'Azure DI layout',
  'chandra-ocr': 'Chandra OCR',
  'chandra-ocr-raw': 'Chandra OCR raw',
  'google-ocr': 'Google OCR',
  'vlm-entity-detect': 'VLM entity detect',
  'vlm-markdown': 'VLM markdown',
  pymupdf: 'PyMuPDF',
  'vlm-bbox-detect': 'VLM bbox detect',
  'vlm-openrouter': 'VLM OpenRouter',
  'okra-ocr': 'okra OCR',
  docling: 'Docling',
  pageindex: 'PageIndex',
  'verify-plugin': 'Verify plugin',
  'textract-ocr': 'Textract OCR',
  'azure-ocr': 'Azure OCR',
  okrapdf: 'okraPDF',
};

const legacyPluginIdAliases: Record<string, string> = {
  textlayer: 'text-layer',
};

function catalogPluginIdForLegacyId(pluginId: string): string {
  return legacyPluginIdAliases[pluginId] ?? pluginId;
}

function titleizePluginId(pluginId: string): string {
  return pluginId
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) =>
      part.toUpperCase() === part ? part : `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`,
    )
    .join(' ');
}

function legacyPluginName(pluginId: string): string {
  return legacyPluginNameOverrides[pluginId] ?? titleizePluginId(pluginId);
}

function surfacesByLegacyPluginId(
  surfaces: readonly LegacyDescriptorSurface[],
): Map<string, LegacyDescriptorSurface[]> {
  const byPluginId = new Map<string, LegacyDescriptorSurface[]>();
  for (const surface of surfaces) {
    for (const pluginId of surface.legacyPluginIds) {
      const pluginSurfaces = byPluginId.get(pluginId) ?? [];
      pluginSurfaces.push(surface);
      byPluginId.set(pluginId, pluginSurfaces);
    }
  }
  return byPluginId;
}

function legacyPluginReferencesByCatalogId(
  surfaces: readonly LegacyDescriptorSurface[],
): Map<string, LegacyPluginReference[]> {
  const byCatalogId = new Map<string, LegacyPluginReference[]>();
  for (const surface of surfaces) {
    for (const legacyId of surface.legacyPluginIds) {
      const catalogId = catalogPluginIdForLegacyId(legacyId);
      const references = byCatalogId.get(catalogId) ?? [];
      references.push({ legacyId, surface });
      byCatalogId.set(catalogId, references);
    }
  }
  return byCatalogId;
}

function toLegacyPluginCatalogRow(
  pluginId: string,
  surfaces: readonly LegacyDescriptorSurface[],
  item: CatalogItem | undefined,
): CatalogPluginRow {
  const primarySurface = surfaces[0];
  const primaryShape: { role: CatalogRole; port: CatalogPort | null } = primarySurface
    ? legacySurfaceShape[primarySurface.kind]
    : { role: 'integration', port: null };
  return {
    id: pluginId,
    name: item?.name ?? legacyPluginName(pluginId),
    role: item?.role ?? primaryShape.role,
    status: item?.status ?? 'supported',
    description:
      item?.oneLiner ??
      `Legacy plugin surfaced from ${surfaces.map((surface) => surface.name).join(', ')} while okra-plugin.jsonc backfill is pending.`,
    href: `/plugins/${pluginId}`,
    port: item?.port ?? primaryShape.port,
    source_kind: 'legacy_surface',
    trust: 'unknown',
    runtime: null,
    format: null,
    allowed_hosts: item?.allowedHosts ?? [],
    npm_package: item?.npmPackage ?? null,
    package_path: item?.packagePath ?? null,
    descriptor_id: null,
    legacy_ids: [pluginId],
    legacy_surface_ids: uniqueStrings(surfaces.map((surface) => surface.id)),
    legacy_surface_paths: uniqueStrings(surfaces.map((surface) => surface.path)),
    tags: [...new Set([...(item?.tags ?? []), 'legacy-surface'])],
  };
}

export function loadLegacyPluginCatalogRows(
  items: readonly CatalogItem[],
  descriptors: readonly CatalogPluginDescriptor[] = pluginDescriptors,
  legacySurfaces: readonly LegacyDescriptorSurface[] = legacyDescriptorSurfaces,
): readonly CatalogPluginRow[] {
  const descriptorIds = new Set(descriptors.map((descriptor) => descriptor.id));
  const itemById = new Map(items.map((item) => [item.id, item]));
  const rows: CatalogPluginRow[] = [];
  for (const [pluginId, surfaces] of surfacesByLegacyPluginId(legacySurfaces)) {
    const catalogPluginId = catalogPluginIdForLegacyId(pluginId);
    if (descriptorIds.has(catalogPluginId)) continue;
    rows.push(toLegacyPluginCatalogRow(catalogPluginId, surfaces, itemById.get(catalogPluginId)));
  }
  return rows.sort((a, b) => a.id.localeCompare(b.id));
}

function loadEcosystemCatalogUnsafe({
  sectionOrder,
  catalogBySection,
  descriptors = pluginDescriptors,
  legacySurfaces = legacyDescriptorSurfaces,
}: LoadEcosystemCatalogOptions): LoadedEcosystemCatalog {
  const allCatalogItems = sectionOrder.flatMap((section) => [...catalogBySection[section]]);
  const catalogById = Object.fromEntries(allCatalogItems.map((item) => [item.id, item])) as Record<
    string,
    CatalogItem
  >;
  const descriptorRows = loadPluginCatalogRows(allCatalogItems, descriptors, legacySurfaces);
  const legacyRows = loadLegacyPluginCatalogRows(allCatalogItems, descriptors, legacySurfaces);

  return {
    catalogBySection,
    allCatalogItems,
    catalogById,
    descriptorCatalogItems: loadDescriptorCatalogItems(descriptors),
    pluginCatalogRows: [...descriptorRows, ...legacyRows],
    legacyDescriptorSurfaces: legacySurfaces,
  };
}

export function loadEcosystemCatalogEffect(
  options: LoadEcosystemCatalogOptions,
): Effect.Effect<LoadedEcosystemCatalog> {
  return Effect.sync(() => loadEcosystemCatalogUnsafe(options));
}

export function loadEcosystemCatalog(options: LoadEcosystemCatalogOptions): LoadedEcosystemCatalog {
  return Effect.runSync(loadEcosystemCatalogEffect(options));
}

function getPluginCatalogRowUnsafe(
  rows: readonly CatalogPluginRow[],
  id: string,
): CatalogPluginRow | undefined {
  return rows.find(
    (row) => row.id === id || row.descriptor_id === id || row.legacy_ids.includes(id),
  );
}

export function getPluginCatalogRowEffect(
  rows: readonly CatalogPluginRow[],
  id: string,
): Effect.Effect<CatalogPluginRow | undefined> {
  return Effect.sync(() => getPluginCatalogRowUnsafe(rows, id));
}

export function getPluginCatalogRow(
  rows: readonly CatalogPluginRow[],
  id: string,
): CatalogPluginRow | undefined {
  return Effect.runSync(getPluginCatalogRowEffect(rows, id));
}

export function pluginCatalogSourceBadgeLabel(source: CatalogPluginRowSourceKind): string {
  return source === 'descriptor' ? 'descriptor' : 'legacy';
}

export function pluginCatalogSourceDetailLabel(
  row: Pick<CatalogPluginRow, 'source_kind' | 'descriptor_id' | 'legacy_surface_ids'>,
): string {
  if (row.source_kind === 'descriptor') return `okra-plugin.jsonc · ${row.descriptor_id}`;
  return `legacy surface · ${row.legacy_surface_ids.join(', ')}`;
}

export function pluginCatalogTrustBadgeMeta(
  row: Pick<CatalogPluginRow, 'trust' | 'allowed_hosts'>,
): CatalogPluginTrustBadgeMeta {
  if (row.trust !== 'unknown') {
    return {
      label: row.allowed_hosts.length
        ? `${row.trust} / allowlisted`
        : `${row.trust} / zero-network`,
      badgeVariant: row.allowed_hosts.length ? 'warning' : 'success',
    };
  }
  return {
    label: row.allowed_hosts.length ? 'unknown / allowlisted' : 'unknown / zero-network',
    badgeVariant: row.allowed_hosts.length ? 'warning' : 'success',
  };
}

function normalizeCatalogFilter(value: string | undefined): string | undefined {
  return value?.trim().toLowerCase() || undefined;
}

export function normalizeCatalogPluginSourceFilter(
  value: string | undefined,
): CatalogPluginSourceFilter | undefined {
  const normalized = normalizeCatalogFilter(value);
  if (!normalized) return undefined;
  return catalogPluginSourceTabs.some((tab) => tab.id === normalized)
    ? (normalized as CatalogPluginSourceFilter)
    : undefined;
}

export function normalizeCatalogPluginRoleFilter(
  value: string | undefined,
): CatalogPluginRoleFilter | undefined {
  const normalized = normalizeCatalogFilter(value);
  if (!normalized) return undefined;
  return catalogPluginRoleTabs.some((tab) => tab.id === normalized)
    ? (normalized as CatalogPluginRoleFilter)
    : undefined;
}

export function countPluginCatalogRowsBySource(
  rows: readonly CatalogPluginRow[],
): Record<CatalogPluginSourceFilter, number> {
  return {
    all: rows.length,
    descriptor: rows.filter((row) => row.source_kind === 'descriptor').length,
    legacy_surface: rows.filter((row) => row.source_kind === 'legacy_surface').length,
  };
}

export function countPluginCatalogRowsByRole(
  rows: readonly CatalogPluginRow[],
): Record<CatalogPluginRoleFilter, number> {
  return {
    all: rows.length,
    parser: rows.filter((row) => row.role === 'parser').length,
    source: rows.filter((row) => row.role === 'source').length,
    integration: rows.filter((row) => row.role === 'integration').length,
  };
}

function filterPluginCatalogRowsUnsafe(
  rows: readonly CatalogPluginRow[],
  options: FilterPluginCatalogRowsOptions = {},
): readonly CatalogPluginRow[] {
  const role = normalizeCatalogFilter(options.role);
  const status = normalizeCatalogFilter(options.status);
  const source = normalizeCatalogFilter(options.source);
  return rows
    .filter((row) => (role && role !== 'all' ? row.role === role : true))
    .filter((row) => (status && status !== 'all' ? row.status === status : true))
    .filter((row) => (source && source !== 'all' ? row.source_kind === source : true));
}

export function filterPluginCatalogRowsEffect(
  rows: readonly CatalogPluginRow[],
  options: FilterPluginCatalogRowsOptions = {},
): Effect.Effect<readonly CatalogPluginRow[]> {
  return Effect.sync(() => filterPluginCatalogRowsUnsafe(rows, options));
}

export function filterPluginCatalogRows(
  rows: readonly CatalogPluginRow[],
  options: FilterPluginCatalogRowsOptions = {},
): readonly CatalogPluginRow[] {
  return Effect.runSync(filterPluginCatalogRowsEffect(rows, options));
}
